/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { ElectricalSettingsState } from './electrical-settings-reducer';
import { Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';
import { IElectricalSettingsFacade } from '../../../../../data-access/src/lib/domain/facades-models/electrical-settings-facade-interface';
import { BackendService } from '../../../../../utils/src/lib/sysque-backend/backend.service';
import {
  ElectricalSettingsInformation,
  ElectricalSettingsObjectInformation,
} from '../../models/electrical-settings-information';
import { ElectricalSettingsStatusResult } from '../../models/electrical-settings-return';
import { ElectricalSettingsObject } from '../../models/electrical-settings-shortcuts';
import {
  GetElectricalShortcutsModifiersInformation,
  GetElectricalSystemsInformation,
  GetInvalidShortcuts,
} from './electrical-settings-actions';
import {
  selectElectricalInformation,
  selectElectricalShortcuts,
  selectInvalidShortcuts,
} from './electrical-settings-selector';
import { LocalStorageService } from '../../../../../utils/src/lib/local-storage/local-storage.service';
import { GlobalConstants } from '../../constants/global-constants';
import { CallsService } from '../../../../../utils/src/lib/backend-calls/calls-backend-service';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';

@Injectable({
  providedIn: 'root',
})
export class ElectricalSettingsFacade implements IElectricalSettingsFacade {
  constructor(
    private store: Store<ElectricalSettingsState>,
    private backendService: BackendService,
    private localStorage: LocalStorageService,
    private service: CallsService
  ) {}

  public onWindowResize(height: number): void {
    this.service.onWindowResize(GlobalConstants.ChangeHeight, height).pipe(take(1)).subscribe();
  }

  public getElectricalSystemsInformation(): Observable<ElectricalSettingsObjectInformation> {
    this.store.dispatch(GetElectricalSystemsInformation());
    return this.store.select(selectElectricalInformation);
  }

  public getElectricalShortcutsModifiersInformation(): Observable<ElectricalSettingsObject> {
    this.store.dispatch(GetElectricalShortcutsModifiersInformation());
    return this.store.select(selectElectricalShortcuts);
  }

  public getInvalidShortcuts(
    electricalSettingsChanges: ElectricalSettingsInformation
  ): Observable<ElectricalSettingsStatusResult> {
    this.store.dispatch(GetInvalidShortcuts({ electricalSettings: electricalSettingsChanges }));
    return this.store.select(selectInvalidShortcuts);
  }

  public applyPreferences(electricalSettingsChanges: ElectricalSettingsInformation): void {
    this.backendService.onApplyPreferencesClick(electricalSettingsChanges);
  }

  public closeWindow(): void {
    this.backendService.onCloseWindow().pipe(take(1)).subscribe();
  }

  public updateLocalStorage(key: string, value: any): void {
    this.localStorage.updateStorage(key, value);
  }

  public getState(): SysqueElectricalState {
    return this.localStorage.getStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY) as SysqueElectricalState;
  }
}
